// Subscription precedence calculation utility

interface UserSubscriptionData {
  // Legacy fields
  isPremium?: boolean;
  subscriptionTier?: string;
  subscriptionStatus?: string;
  billingProvider?: string;
  stripeSubscriptionId?: string;
  
  // New multi-source fields
  individual?: {
    subscriptionTier: string;
    subscriptionStatus: string;
    billingProvider: string;
    stripeSubscriptionId?: string;
    periodEnd?: Date;
  };
  organization?: {
    organizationId: string;
    teamId?: string;
    allocatedSeat?: {
      tier: string;
      assignedAt: Date;
      assignedBy: string;
      status: 'active' | 'revoked' | 'pending';
    };
  };
  role?: string;
}

export interface EffectiveSubscription {
  tier: string;
  source: 'individual' | 'organization' | 'admin' | 'legacy' | 'default';
  lastCalculated: Date;
  details?: {
    individual?: {
      tier: string;
      status: string;
      active: boolean;
    };
    organization?: {
      tier: string;
      teamId?: string;
      active: boolean;
    };
    admin?: boolean;
  };
}

const TIER_HIERARCHY = {
  'free': 0,
  'plus': 1,
  'pro': 2,
  'admin': 3
} as const;

export function calculateEffectiveSubscription(userData: UserSubscriptionData): EffectiveSubscription {
  let highestTier = 'free';
  let source: EffectiveSubscription['source'] = 'default';
  
  const details: EffectiveSubscription['details'] = {};
  
  // Check admin role first (always overrides)
  if (userData.role === 'admin') {
    return {
      tier: 'admin',
      source: 'admin',
      lastCalculated: new Date(),
      details: { admin: true }
    };
  }
  
  // Check legacy subscription (for backward compatibility)
  if (userData.subscriptionTier && userData.isPremium && userData.subscriptionStatus === 'active') {
    const legacyTier = userData.subscriptionTier;
    if (TIER_HIERARCHY[legacyTier as keyof typeof TIER_HIERARCHY] > TIER_HIERARCHY[highestTier as keyof typeof TIER_HIERARCHY]) {
      highestTier = legacyTier;
      source = 'legacy';
    }
  }
  
  // Check individual subscription
  if (userData.individual?.subscriptionStatus === 'active') {
    const individualTier = userData.individual.subscriptionTier;
    details.individual = {
      tier: individualTier,
      status: userData.individual.subscriptionStatus,
      active: true
    };
    
    if (TIER_HIERARCHY[individualTier as keyof typeof TIER_HIERARCHY] > TIER_HIERARCHY[highestTier as keyof typeof TIER_HIERARCHY]) {
      highestTier = individualTier;
      source = 'individual';
    } else if (TIER_HIERARCHY[individualTier as keyof typeof TIER_HIERARCHY] === TIER_HIERARCHY[highestTier as keyof typeof TIER_HIERARCHY]) {
      // Equal tiers - prefer individual for consistency
      source = 'individual';
    }
  }
  
  // Check organization seat allocation
  if (userData.organization?.allocatedSeat?.status === 'active') {
    const orgTier = userData.organization.allocatedSeat.tier;
    details.organization = {
      tier: orgTier,
      teamId: userData.organization.teamId,
      active: true
    };
    
    if (TIER_HIERARCHY[orgTier as keyof typeof TIER_HIERARCHY] > TIER_HIERARCHY[highestTier as keyof typeof TIER_HIERARCHY]) {
      highestTier = orgTier;
      source = 'organization';
    }
  }
  
  return {
    tier: highestTier,
    source,
    lastCalculated: new Date(),
    details
  };
}

export function getSubscriptionDisplayInfo(effective: EffectiveSubscription): {
  displayTier: string;
  displaySource: string;
  recommendations: string[];
} {
  const recommendations: string[] = [];
  
  // Website allocations do not replace verified Apple purchases in iOS 1.7.

  const sourceDisplayMap = {
    'individual': 'Individual Subscription',
    'organization': 'Team Allocation',
    'admin': 'Admin Access',
    'legacy': 'Legacy Account',
    'default': 'Free Tier'
  };
  
  return {
    displayTier: effective.tier.toUpperCase(),
    displaySource: sourceDisplayMap[effective.source],
    recommendations
  };
}

// Utility to migrate legacy subscription data to new format
export function migrateLegacySubscription(userData: UserSubscriptionData): UserSubscriptionData {
  // If user has legacy subscription data but no individual data, migrate it
  if (userData.subscriptionTier && userData.isPremium && !userData.individual) {
    const migrated = { ...userData };
    migrated.individual = {
      subscriptionTier: userData.subscriptionTier,
      subscriptionStatus: userData.subscriptionStatus || 'active',
      billingProvider: userData.billingProvider || 'legacy',
      ...(userData.stripeSubscriptionId && { stripeSubscriptionId: userData.stripeSubscriptionId })
    };
    return migrated;
  }
  return userData;
}